<script setup lang="ts">
import { ChevronLeft, ChevronRight } from 'lucide-vue-next';
import { computed, onMounted, onUnmounted, ref } from 'vue';

import type { Language } from '@/types/mode-list-locale';

import { convertUrlToResizer } from '@/utils/utils.js';

const props = defineProps<{
  images: Array<{ credit: string | null; lang: Language; name: string; url: string }>;
  link: string;
  buttonsPersisted?: boolean;
}>();

const emits = defineEmits<{
  (e: 'isPersisted'): void;
}>();

const currentIndex = ref(0);
const carouselElement = ref<HTMLElement>();

const totalImages = computed(() => props.images.length);

function goToSlide(event: Event, index: number) {
  event.stopPropagation();
  if (index >= 0 && index < totalImages.value) {
    currentIndex.value = index;
  }
  emits('isPersisted');
}

function nextSlide(event: Event) {
  event.stopPropagation();
  if (currentIndex.value === totalImages.value - 1)
    return;
  currentIndex.value = Math.min(currentIndex.value + 1, totalImages.value - 1);
  emits('isPersisted');
}

function prevSlide(event: Event) {
  event.stopPropagation();
  if (currentIndex.value === 0)
    return;
  currentIndex.value = Math.max(currentIndex.value - 1, 0);
  emits('isPersisted');
}

function handleKeyDown(event: KeyboardEvent) {
  event.stopPropagation();
  if (event.key === 'ArrowLeft') {
    prevSlide(event);
  }
  else if (event.key === 'ArrowRight') {
    nextSlide(event);
  }
  else if (event.key === 'Enter') {
    openDetails(props.link);
  }
}

function openDetails(link: string) {
  window.open(link, '_blank');
}

onMounted(() => {
  if (carouselElement.value) {
    carouselElement.value.addEventListener('keydown', handleKeyDown);
  }
});

onUnmounted(() => {
  if (carouselElement.value) {
    carouselElement.value.removeEventListener('keydown', handleKeyDown);
  }
});
</script>

<template>
  <div v-if="props.images.length > 0" ref="carouselElement" role="button" class="image-carousel" tabindex="0" @keydown="handleKeyDown">
    <div
      class="carousel-inner"
      :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
      aria-roledescription="carousel"
      aria-label="photos"
      @click="openDetails(props.link)"
    >
      <div v-for="(image, index) in props.images" :key="index" role="group" class="carousel-slide" aria-roledescription="slide" :aria-label="`picture ${currentIndex + 1} of ${totalImages}`">
        <img
          :src="convertUrlToResizer(image.url)"
          :alt="image.credit || `Slide ${index + 1}`"
          class="carousel-image"
          :fetchpriority="index === 0 ? 'high' : 'auto'"
          :loading="index === 0 ? 'eager' : 'lazy'"
          draggable="false"
        >
      </div>
    </div>

    <button type="button" class="nav-button left" :class="{ persisted: props.buttonsPersisted, invisible: currentIndex === 0 }" @click.self="(e) => prevSlide(e)">
      <ChevronLeft color="black" :size="18" />
    </button>
    <div class="carousel-dots">
      <button
        v-for="(_, index) in props.images"
        :key="index"
        class="dot"
        :class="{ active: index === currentIndex }"
        :aria-label="`Go to slide ${index + 1}`"
        @click.self="(e) => goToSlide(e, index)"
      />
    </div>
    <button type="button" class="nav-button right" :class="{ persisted: props.buttonsPersisted, invisible: currentIndex + 1 === totalImages }" @click.self="(e) => nextSlide(e)">
      <ChevronRight color="black" :size="18" />
    </button>
  </div>
  <div v-else class="carousel-empty">
    <div class="carousel-slide">
      <img
        :src="convertUrlToResizer('https://images.unsplash.com/photo-1521138054413-5a47d349b7af?q=80&w=2303&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')"
        alt="picture placeholder"
        class="carousel-image"
        fetchpriority="high"
        draggable="false"
      >
    </div>
  </div>
</template>

<style scoped>
.image-carousel {
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 800px;
  margin: auto;
  overflow: hidden;
  border-top-left-radius: var(--yb-card-gallery-radius);
  border-top-right-radius: var(--yb-card-gallery-radius);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  outline: none;
  -webkit-user-select: none; /* Disable text selection during swipe */
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  &:hover {
    .nav-button {
      visibility: visible;
    }
  }
}

.carousel-inner {
  display: flex;
  transition: transform 0.5s ease-in-out;
  /* touch-action: none; */ /* If you want to completely disable browser default pan/zoom */
}

.carousel-slide {
  flex: 0 0 100%; /* Each slide takes full width */
  width: 100%;
  box-sizing: border-box;
}

.carousel-image {
  display: block; /* Removes bottom space under image */
  width: 100%;
  height: auto;
  max-height: 500px;
  object-fit: cover;
}

.nav-button {
  visibility: hidden;
  position: absolute;
  z-index: 8;
  top: 40%;
  translate: 0 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 35px;
  width: 35px;
  border-radius: 999px;
  border: none;
  outline: none;
  background-color: hsla(0, 0%, 100%, 0.9);
  box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
  transition: all 0.2s ease;
  transform-origin: center center;

  &:hover {
    background-color: white;
    transform: scale(1.1) translateZ(0);
  }

  &.left {
    left: 10px;
    svg {
      left: 46%;
      transform: translate(-50%, -50%);
    }
  }

  &.right {
    right: 10px;
    svg {
      left: 52%;
      transform: translate(-50%, -50%);
    }
  }

  svg {
    position: absolute;
    top: 52%;
    pointer-events: none;
  }
}

.persisted {
  visibility: visible;
}

.invisible {
  opacity: 0;
}

.carousel-dots {
  position: absolute;
  bottom: 15px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 5;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: rgba(188, 183, 183, 1);
  border: none;
  padding: 0;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.dot:hover {
  background-color: white;
}

.dot.active {
  background-color: white;
}

.carousel-empty {
  padding: 20px;
  text-align: center;
  color: #666;
  border: 1px dashed #ccc;
  border-radius: 8px;
}
</style>
